import { Trans } from '@lingui/react/macro';
import { ChevronRight, Loader2, Wrench } from 'lucide-react';
import { motion } from 'motion/react';
import { useState } from 'react';

import type { ToolState } from '../tool-provider';

export interface GenericToolCallProps {
  toolName: string;
  args: unknown;
  content?: unknown;
  artifact?: unknown;
  state: ToolState;
  error?: string;
}

function stringify(value: unknown): string {
  if (value === undefined || value === null) {
    return '';
  }

  if (typeof value === 'string') {
    return value;
  }

  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function Section({ label, value }: { label: React.ReactNode; value: string }) {
  if (!value) {
    return null;
  }

  return (
    <div className='flex flex-col gap-1'>
      <span className='text-xs font-medium text-chat-muted-foreground'>
        {label}
      </span>
      <pre className='max-h-64 overflow-auto whitespace-pre-wrap break-words rounded border border-chat-border bg-chat-background/50 p-2 text-xs text-chat-foreground'>
        {value}
      </pre>
    </div>
  );
}

/**
 * Fallback renderer for tool calls that have no dedicated component in the
 * registry (e.g. MCP / HTTP / other dynamic tools). Used in the admin inbox so
 * support staff can inspect what the agent called and what came back. Not wired
 * into the customer-facing widget.
 */
export function GenericToolCall({
  toolName,
  args,
  content,
  artifact,
  state,
  error,
}: GenericToolCallProps) {
  const [open, setOpen] = useState(false);
  const isRunning = state === 'call' || state === 'call-partial';
  const isError = state === 'error';

  return (
    <motion.div
      className='w-full overflow-hidden rounded-3xl border border-chat-border bg-chat-background-secondary'
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <button
        type='button'
        onClick={() => setOpen(prev => !prev)}
        className='flex w-full items-center gap-2 p-3 text-left'
      >
        <span className='flex size-6 shrink-0 items-center justify-center rounded-md border border-chat-border bg-chat-background text-chat-foreground [&>svg]:size-3.5'>
          {isRunning ? <Loader2 className='animate-spin' /> : <Wrench />}
        </span>
        <span className='flex min-w-0 flex-col'>
          <span className='truncate text-sm font-medium text-chat-foreground'>
            {toolName}
          </span>
          <span className='text-xs text-chat-muted-foreground'>
            {isError ? (
              <Trans>Tool call failed</Trans>
            ) : isRunning ? (
              <Trans>Running…</Trans>
            ) : (
              <Trans>Tool call</Trans>
            )}
          </span>
        </span>
        <ChevronRight
          className={`ml-auto size-4 shrink-0 text-chat-muted-foreground transition-transform ${
            open ? 'rotate-90' : ''
          }`}
        />
      </button>

      {open && (
        <div className='flex flex-col gap-3 border-t border-chat-border p-3'>
          <Section label={<Trans>Arguments</Trans>} value={stringify(args)} />
          <Section label={<Trans>Result</Trans>} value={stringify(content)} />
          <Section
            label={<Trans>Artifact</Trans>}
            value={stringify(artifact)}
          />
          {error && (
            <Section label={<Trans>Error</Trans>} value={stringify(error)} />
          )}
        </div>
      )}
    </motion.div>
  );
}
